import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../../styles/Admin.css';

// Components
import HeroGreeting from '../admin/dashboard/HeroGreeting';
import KPICards from '../admin/dashboard/KPICards';
import AttentionPanel from '../admin/dashboard/AttentionPanel';
import QuickActions from '../admin/dashboard/QuickActions';
import BusinessInsights from '../admin/dashboard/BusinessInsights';
import RecentActivity from '../admin/dashboard/RecentActivity';
import DashboardFooter from '../admin/dashboard/DashboardFooter';
import MobileHeader from './dashboard/MobileHeader';
import MobileBottomNav from './dashboard/MobileBottomNav';
import WelcomeCelebration from './dashboard/WelcomeCelebration';

import { useAuth } from '../../context/AuthContext'; // Import useAuth
import Loader from '../common/Loader'; // Import Premium Loader

const AdminDashboard = () => {
    const { user } = useAuth(); // Get user
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ todaySales: 0, monthSales: 0, totalPending: 0, pendingBillCount: 0, todayBillCount: 0 });
    const [alerts, setAlerts] = useState([]);
    const [insights, setInsights] = useState({});
    const [recentActivity, setRecentActivity] = useState([]);
    const [allBills, setAllBills] = useState([]); // Store bills for chart

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            // Parallel Fetching
            const [billsRes, jewRes, customersRes] = await Promise.all([
                axios.get('/api/bills'),
                axios.get('/api/jewellery'),
                axios.get('/api/customers')
            ]);

            const bills = billsRes.data;
            const items = jewRes.data;
            const customers = customersRes.data;

            setAllBills(bills); // Save for chart
            processStats(bills);
            processAlerts(bills, items);
            processInsights(bills, customers);
            processRecentActivity(bills, items, customers);

            setLoading(false);
        } catch (error) {
            console.error("Error loading dashboard:", error);
            setLoading(false);
        }
    };

    // 1. Process Stats (KPIs)
    const processStats = (bills) => {
        const today = new Date().toISOString().split('T')[0];
        const currentMonth = new Date().getMonth();

        let todaySales = 0;
        let todayCount = 0;
        let pending = 0;
        let pendingCount = 0;
        let monthSales = 0;

        bills.forEach(bill => {
            const billDate = new Date(bill.billDate || bill.createdAt).toISOString().split('T')[0];
            const billMonth = new Date(bill.billDate || bill.createdAt).getMonth();
            const total = bill.totalAmount || bill.amount;

            // Payment Status logic (System vs Legacy)
            let isPending = false;
            let dueAmount = 0;

            if (bill.source === 'LEGACY') {
                isPending = bill.paymentStatus !== 'Paid';
                // Estimate due based on simple logic if not tracked
                dueAmount = bill.paymentStatus === 'Paid' ? 0 : (bill.amount || 0);
            } else {
                // System Bill
                if (bill.paymentDetails) {
                    dueAmount = bill.paymentDetails.balance;
                    isPending = bill.paymentDetails.status !== 'Paid';
                } else {
                    // Backward compat
                    isPending = bill.paymentStatus !== 'Paid';
                    dueAmount = isPending ? total : 0;
                }
            }

            // Today Sales
            if (billDate === today) {
                todaySales += total;
                todayCount++;
            }

            // Month Sales
            if (billMonth === currentMonth) {
                monthSales += total;
            }

            // Pending
            if (isPending) {
                pending += dueAmount;
                pendingCount++;
            }
        });

        setStats({
            todaySales,
            todayBillCount: todayCount,
            totalPending: pending,
            pendingBillCount: pendingCount,
            monthSales
        });
    };

    // 2. Process Alerts (Attention Required)
    const processAlerts = (bills, items) => {
        const alertsList = [];
        const today = new Date();

        // Check Pending Bills > 7 days
        let oldPendingCount = 0;
        bills.forEach(bill => {
            const billDate = new Date(bill.billDate || bill.createdAt);
            const diffTime = Math.abs(today - billDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            let isPending = false;
            if (bill.source === 'LEGACY') {
                isPending = bill.paymentStatus !== 'Paid';
            } else {
                isPending = bill.paymentDetails ? bill.paymentDetails.status !== 'Paid' : bill.paymentStatus !== 'Paid';
            }

            if (isPending && diffDays > 7) {
                oldPendingCount++;
            }
        });

        if (oldPendingCount > 0) {
            const alertText = oldPendingCount + " bills pending for more than 7 days";
            const alertObj = {
                type: 'danger',
                text: alertText,
                actionText: 'View',
                link: '/billing/history'
            };
            alertsList.push(alertObj);
        }

        // Check Low Stock (Draft Items)
        const draftItems = items.filter(i => i.status === 'Draft').length;
        if (draftItems > 0) {
            const draftText = draftItems + " items in Draft (incomplete details)";
            const draftAlert = {
                type: 'warning',
                text: draftText,
                actionText: 'Fix',
                link: '/jewellery'
            };
            alertsList.push(draftAlert);
        }

        // Gold Rate (Mock check)
        const goldAlert = {
            type: 'success',
            text: 'Gold rate updated today',
            actionText: 'OK',
            link: '/live-gold-rate'
        };
        alertsList.push(goldAlert);

        setAlerts(alertsList);
    };

    // 3. Process Insights
    const processInsights = (bills, customers) => {
        // Top Selling Category Logic
        const categoryCount = {};
        let totalBillValue = 0;
        let billCount = 0;

        // Peak Hour Logic
        const hourCount = new Array(24).fill(0);

        bills.forEach(bill => {
            // Categories
            if (bill.items) {
                bill.items.forEach(item => {
                    const cat = item.category || 'Other';
                    categoryCount[cat] = (categoryCount[cat] || 0) + 1;
                });
            }

            // Avg Value
            totalBillValue += (bill.totalAmount || bill.amount || 0);
            billCount++;

            // Peak Hour
            const date = new Date(bill.createdAt || bill.billDate); // Legacy might not have time
            if (date) {
                const hour = date.getHours();
                hourCount[hour]++;
            }
        });

        // Resolve Top Item
        let topItem = 'N/A';
        let maxCount = 0;
        for (const [cat, count] of Object.entries(categoryCount)) {
            if (count > maxCount) {
                maxCount = count;
                topItem = cat;
            }
        }

        // Resolve Peak Time
        let peakHour = 0;
        let maxHourCount = 0;
        hourCount.forEach((count, idx) => {
            if (count > maxHourCount) {
                maxHourCount = count;
                peakHour = idx;
            }
        });
        const peakTimeStr = peakHour + ":00 - " + (peakHour + 1) + ":00";

        // Customers Logic
        const currentMonth = new Date().getMonth();
        let newCust = 0;
        let retCust = 0; // Simplified

        customers.forEach(c => {
            if (new Date(c.createdAt).getMonth() === currentMonth) newCust++;
            else retCust++;
        });

        setInsights({
            topItem,
            avgBillValue: billCount ? totalBillValue / billCount : 0,
            peakTime: peakTimeStr,
            newCustomers: newCust,
            returningCustomers: retCust
        });
    };

    // 4. Process Activity
    const processRecentActivity = (bills, items, customers) => {
        // Combine all events
        const events = [];

        // Bills
        bills.forEach(b => {
            const billNum = b.billNumber || 'Legacy';
            const amount = b.totalAmount || b.amount;
            events.push({
                date: new Date(b.createdAt || b.billDate),
                type: 'BILL',
                text: "Bill #" + billNum + " created \u2014 \u20B9" + (amount ? amount.toLocaleString() : "0"),
                subtext: b.paymentStatus === 'Partial' ? '(Partial)' : ''
            });

            // Payments from history
            if (b.paymentHistory) {
                b.paymentHistory.forEach(h => {
                    events.push({
                        date: new Date(h.date),
                        type: 'PAYMENT',
                        text: "Payment received \u2014 \u20B9" + h.amount.toLocaleString() + " (" + h.mode + ")",
                        subtext: "For Bill #" + billNum
                    });
                });
            }
        });

        // Items
        items.forEach(i => {
            events.push({
                date: new Date(i.createdAt),
                type: 'ITEM',
                text: "Jewellery added \u2014 " + i.name,
                subtext: i.category
            });
        });

        // Customers
        customers.forEach(c => {
            events.push({
                date: new Date(c.createdAt),
                type: 'CUSTOMER',
                text: "New customer added \u2014 " + c.firstName + " " + c.lastName,
                subtext: c.city
            });
        });

        // Sort desc
        events.sort((a, b) => b.date - a.date);

        // Take top 5
        setRecentActivity(events.slice(0, 5));
    };

    if (loading) return <Loader text="Loading Dashboard..." />;

    return (
        <div>
            <div className="section">
                <HeroGreeting />
            </div>

            <div className="section">
                <KPICards stats={stats} role={user?.role} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '30px' }}>
                {/* Left Column */}
                <div>
                    <div className="section">
                        <AttentionPanel alerts={alerts} />
                    </div>
                    <div className="section">
                        <QuickActions />
                    </div>
                    <div className="section">
                        <RecentActivity activities={recentActivity} />
                    </div>
                </div>

                {/* Right Column */}
                <div>
                    <BusinessInsights insights={insights} />
                </div>
            </div>

            <DashboardFooter />
        </div>
    );
};

export default AdminDashboard;
